// NOMBASE — Apps launcher (Launchpad-style)
// A full-viewport launcher triggered from the sidebar. Tiles work like
// macOS Launchpad: you can drag them around to rearrange, drag them onto
// the sidebar to pin, or tap a "+" shortcut on each tile. The backdrop
// is a soft dark tint — NO blur — so the main app stays crisp behind it.
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  LayoutGrid, Search,
  Settings, Users, Scale,
  Truck, GitBranch, BarChart3, Folder,
  ShieldCheck, CalendarRange, ShoppingBag, FileText,
  Plus, X,
  Save, Users2, RotateCcw, Loader2,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { getIcon } from '@/lib/iconRegistry';
import DriveIcon from '@/components/icons/DriveIcon';
import { queryClient } from '@/lib/queryClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import IconTip from '@/components/shared/IconTip';

// Muted palette — deliberately no blue per user preference.
const COLORS = {
  slate:       'bg-slate-900 text-white',
  neutral:     'bg-slate-100 text-slate-700',
  indigo:      'bg-indigo-100 text-indigo-700',
  violet:      'bg-violet-100 text-violet-700',
  zinc:        'bg-zinc-100 text-zinc-700',
  // For app tiles that carry their own full-color artwork (e.g. the
  // Drive icon). A soft ring keeps the tile grid rhythm without
  // competing with the logo colors.
  transparent: 'bg-white ring-1 ring-slate-100 text-slate-700',
};

// App catalog — every navigable page the user might want to pin to the
// sidebar. Each entry carries its iconName (string) so we can both render
// it now and pass it into the sidebar config when added.
const ALL_APPS = [
  // === Brand apps ===
  { id: 'search',      label: 'Search',     iconName: 'Search',       color: 'slate',   route: '/requests/marketplace', roles: ['brand'],                       category: 'Discovery' },
  { id: 'formulas',    label: 'Files',      iconName: 'Drive',        color: 'slate',   route: '/development?openFormulas=1', roles: ['brand'],                category: 'Discovery' },

  // === Supplier / Co-manufacturer apps ===
  { id: 'customers',   label: 'Customers',  iconName: 'Users',        color: 'neutral', route: '/customers',             roles: ['supplier', 'comfg'],            category: 'Operations' },
  { id: 'pipeline',    label: 'Pipeline',   iconName: 'GitBranch',    color: 'neutral', route: '/pipeline',              roles: ['supplier', 'comfg'],            category: 'Operations' },
  { id: 'scorecard',   label: 'Scorecard',  iconName: 'BarChart3',    color: 'neutral', route: '/scorecard',             roles: ['supplier', 'comfg'],            category: 'Operations' },
  { id: 'shipments',   label: 'Shipments',  iconName: 'Truck',        color: 'neutral', route: '/shipments',             roles: ['supplier', 'comfg'],            category: 'Operations' },
  { id: 'orders',      label: 'Orders',     iconName: 'ShoppingBag',  color: 'neutral', route: '/orders',                roles: ['supplier'],                     category: 'Operations' },

  // === Profile / Capability ===
  { id: 'capability',  label: 'Capability', iconName: 'Scale',        color: 'violet',  route: '/capability-profile',    roles: ['comfg'],                        category: 'Profile' },
  { id: 'quality',     label: 'Quality',    iconName: 'ShieldCheck',  color: 'neutral', route: '/quality',               roles: ['supplier', 'comfg'],            category: 'Profile' },
  { id: 'requests',    label: 'Requests',   iconName: 'FileText',     color: 'neutral', route: '/requests',              roles: ['supplier', 'comfg'],            category: 'Operations' },

  // === Settings (shared) ===
  { id: 'settings',    label: 'Settings',   iconName: 'Settings',     color: 'zinc',    route: '/setup/company',         roles: ['brand', 'supplier', 'comfg'],  category: 'Settings' },
];

const STATIC_ICON = {
  Search, Users, Scale, Settings, Truck, GitBranch, BarChart3, Folder,
  ShieldCheck, CalendarRange, ShoppingBag, FileText,
  Drive: DriveIcon,
};

function AppTile({ app, onClick, draggable, onAddToSidebar }) {
  const Icon = STATIC_ICON[app.iconName] || getIcon(app.iconName);
  const colorCls = COLORS[app.color] || COLORS.zinc;

  const handleDragStart = (e) => {
    if (!draggable) return;
    // Use 'copyMove' so both sidebar-reorder ('move') and sidebar-add
    // ('copy') drop targets accept the drag without effect mismatch.
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('application/x-nombase-sidebar', JSON.stringify({
      source: 'launcher',
      id: app.id,
      label: app.label,
      href: app.route,
      iconName: app.iconName,
    }));
    // Use the icon square (the colored rounded box inside the tile button)
    // as the ghost so the cursor shows a clean icon while dragging.
    try {
      const iconEl = e.currentTarget.querySelector('button > div');
      if (iconEl) {
        const rect = iconEl.getBoundingClientRect();
        e.dataTransfer.setDragImage(iconEl, rect.width / 2, rect.height / 2);
      }
    } catch { /* noop */ }
  };

  return (
    <div
      className="relative group select-none"
      draggable={draggable || undefined}
      onDragStart={handleDragStart}
      data-testid={`app-tile-${app.id}`}
    >
      <button
        onClick={onClick}
        className="w-full flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-slate-50 active:bg-slate-100 transition-colors focus:outline-none focus:bg-slate-50"
        type="button"
      >
        <div className={cn(
          'w-14 h-14 rounded-2xl flex items-center justify-center shadow-[0_4px_12px_-4px_rgba(0,0,0,0.12)] group-hover:scale-[1.05] group-active:scale-95 transition-transform',
          colorCls
        )}>
          <Icon className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <span className="text-[11px] font-medium text-slate-700 text-center leading-tight max-w-full truncate">
          {app.label}
        </span>
      </button>
      {onAddToSidebar && (
        <IconTip label={`Pin ${app.label} to sidebar`}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddToSidebar({
              id: app.id,
              label: app.label,
              href: app.route,
              iconName: app.iconName,
            });
          }}
          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center transition-opacity text-[10px] bg-slate-900 text-white opacity-0 group-hover:opacity-100 shadow"
          aria-label={`Pin ${app.label} to sidebar`}
          data-testid={`app-add-to-sidebar-${app.id}`}
        >
          <Plus className="w-3 h-3" />
        </button>
        </IconTip>
      )}
    </div>
  );
}

/**
 * @param extraApps         Additional draggable app entries (primary nav items) to show
 * @param isInSidebar       Callback: (id) => boolean
 * @param onAddToSidebar    Callback: (app) => void  — "+" click shortcut
 * @param onRemoveFromSidebar Callback: (id) => void — invoked when a sidebar item is dropped onto the modal.
 * @param onReorderSidebar  Callback: (fromIndex, toIndex) => void — reorder within the modal (unused now but kept for API stability)
 * @param onResetDefault    Callback: () => void
 * @param onApplyTeamDefault Callback: () => void
 * @param isTenantAdmin     Boolean
 * @param currentItems      Array — user's current sidebar order (matches extraApps order)
 * @param teamDefaultItems  Array
 */
export default function AppLauncher({
  trigger, extraApps = [], isInSidebar, onAddToSidebar, onRemoveFromSidebar, onReorderSidebar,
  onResetDefault, onApplyTeamDefault, isTenantAdmin, currentItems = [], teamDefaultItems = [],
}) {
  const [open, setOpen] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const [cardDragOver, setCardDragOver] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useApp();

  // Brand-only MVP — the per-user sidebar-layout cache key is keyed
  // on 'brand' for every user. The role variable is preserved as a
  // const rather than inlined so the storage-key call sites read
  // naturally.
  const role = 'brand';

  const hasTeamDefault = Array.isArray(teamDefaultItems) && teamDefaultItems.length > 0;
  const currentMatchesTeam = hasTeamDefault && JSON.stringify(
    currentItems.map(i => i.id)
  ) === JSON.stringify(teamDefaultItems.map(i => i.id));

  const handleSaveAsTeamDefault = async () => {
    setSavingTeam(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/me/sidebar-default`, { credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: currentItems }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Save failed' }));
        throw new Error(err.detail || 'Save failed');
      }
      queryClient.invalidateQueries({ queryKey: ['tenant-sidebar-default'] });
      toast.success('Saved as team default — new teammates will inherit this order');
    } catch (e) {
      toast.error(e.message || 'Failed to save team default');
    } finally {
      setSavingTeam(false);
    }
  };

  // Tiles shown in the modal = every app that is NOT currently in the
  // sidebar. When a user removes an item from the sidebar (via the × or
  // by dragging it onto this modal), it appears here, ready to be re-pinned.
  const allApps = useMemo(() => {
    const extras = extraApps.map(x => ({
      id: x.id,
      label: x.label,
      iconName: x.iconName,
      color: 'neutral',
      route: x.href,
      roles: [role],
      category: 'Apps',
    }));
    const curated = ALL_APPS.filter(a => a.roles.includes(role));
    const seen = new Set();
    return [...extras, ...curated].filter(a => (seen.has(a.id) ? false : (seen.add(a.id), true)));
  }, [extraApps, role]);

  const availableApps = useMemo(
    () => allApps.filter(a => !isInSidebar?.(a.id)),
    [allApps, isInSidebar]
  );

  const grouped = useMemo(() => {
    const map = new Map();
    availableApps.forEach(a => {
      if (!map.has(a.category)) map.set(a.category, []);
      map.get(a.category).push(a);
    });
    return Array.from(map.entries());
  }, [availableApps]);

  const go = (app) => { setOpen(false); navigate(app.route); };

  return (
    <>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            onClick={() => setOpen(true)}
            aria-label="Open Apps"
            className="flex items-center justify-center w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 active:scale-95 transition-all mb-2"
            data-testid="apps-launcher-trigger"
            type="button"
          >
            {trigger || <LayoutGrid className="w-4 h-4 text-slate-600" strokeWidth={2} />}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="rounded-lg px-3 py-1.5 text-sm font-medium bg-navy-900 text-white border-0 shadow-md"
          sideOffset={12}
        >
          Apps
        </TooltipContent>
      </Tooltip>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen} modal={false}>
        <DialogPrimitive.Portal>
          {/* Overlay is visual only — `pointer-events-none` so drag events
              pass through to the sidebar behind. Click-to-close is handled
              by a dedicated backdrop below that excludes the sidebar area. */}
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-[100] bg-slate-950/40 pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            data-testid="apps-launcher-overlay"
          />
          {/* Click-catcher — covers the viewport EXCEPT the 80px sidebar
              gutter on the left so users can drop tiles onto the sidebar
              without the overlay closing or intercepting. Clicking the
              visible tinted area closes the modal. */}
          <div
            className="fixed inset-y-0 left-[80px] right-0 z-[100]"
            onMouseDown={() => setOpen(false)}
            aria-hidden="true"
          />
          <DialogPrimitive.Content
            className="fixed inset-0 z-[101] flex items-center justify-center p-6 outline-none pointer-events-none data-[state=open]:animate-in data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:fade-in-0"
            data-testid="apps-launcher-dialog"
            // Never auto-close on outside pointer events — users drag
            // tiles across the viewport and onto the sidebar; we handle
            // close explicitly via the click-catcher, X button, or Esc.
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            // Dragover listener at the Content level so any child of the
            // card (header, grid, tile, etc.) still qualifies as a drop
            // target — `preventDefault()` on dragover is mandatory for
            // drop to fire. We accept only our custom MIME so URL/text
            // drags from elsewhere are ignored.
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes('application/x-nombase-sidebar')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }
            }}
          >
            <VisuallyHidden asChild><DialogPrimitive.Title>Apps</DialogPrimitive.Title></VisuallyHidden>
            <VisuallyHidden asChild><DialogPrimitive.Description>Navigate to any page in NOMBASE. Drag tiles to rearrange or pin to the sidebar.</DialogPrimitive.Description></VisuallyHidden>

            {/* Rounded card — Apple-style. Solid white bg, generous radius,
                soft shadow. Content lives inside this card, NOT on the
                raw overlay. `pointer-events-auto` so the card is still
                interactive even though the wrapper is pass-through. */}
            <div
              className={cn(
                'pointer-events-auto relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-[28px] bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] border border-slate-100 overflow-hidden transition-colors',
                cardDragOver && 'ring-2 ring-rose-300 border-rose-200'
              )}
              // HTML5 DnD requires `preventDefault()` on both dragenter
              // AND dragover for the element (and children) to accept
              // drops. We don't filter by MIME here — the drop handler
              // verifies the payload and ignores anything non-sidebar.
              onDragEnter={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (!cardDragOver) setCardDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragLeave={(e) => {
                // Only clear when leaving the card entirely (not moving
                // between child tiles).
                if (!e.currentTarget.contains(e.relatedTarget)) setCardDragOver(false);
              }}
              onDrop={(e) => {
                setCardDragOver(false);
                const raw = e.dataTransfer.getData('application/x-nombase-sidebar');
                if (!raw) return;
                e.preventDefault();
                try {
                  const data = JSON.parse(raw);
                  if (data.source === 'sidebar' && onRemoveFromSidebar) {
                    onRemoveFromSidebar(data.id);
                  }
                } catch { /* noop */ }
              }}
            >
              {/* Close button inside the card header */}
              <DialogPrimitive.Close
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 flex items-center justify-center transition-colors z-10"
                aria-label="Close apps"
              >
                <X className="w-4 h-4" />
              </DialogPrimitive.Close>

              {/* Header */}
              <div className="px-6 pt-6 pb-3 border-b border-slate-100">
                <h2 className="text-[15px] font-semibold text-slate-900">Apps</h2>
                {onAddToSidebar && (
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Drag a tile onto the sidebar to pin · drag a sidebar icon here to unpin · press <kbd className="px-1 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-mono">Esc</kbd> to close
                  </p>
                )}
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto">
                {/* Admin team-default bar */}
                {onAddToSidebar && (isTenantAdmin || hasTeamDefault) && (
                  <div className="mx-6 mt-4 flex items-center gap-2 flex-wrap rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                    <Users2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="text-[11px] font-medium text-slate-600 flex-1 min-w-[140px]">
                      {isTenantAdmin
                        ? (hasTeamDefault ? 'Your team has a default sidebar' : 'Pin a default sidebar for your team')
                        : 'Your team has a recommended layout'}
                    </span>
                    {hasTeamDefault && onApplyTeamDefault && (
                      <button
                        type="button"
                        onClick={onApplyTeamDefault}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 transition-colors"
                        data-testid="apps-apply-team-default"
                        title={currentMatchesTeam ? 'Your sidebar already matches the team default' : 'Apply the team default to your sidebar'}
                      >
                        <RotateCcw className="w-3 h-3" /> Use team default
                      </button>
                    )}
                    {isTenantAdmin && (
                      <button
                        type="button"
                        onClick={handleSaveAsTeamDefault}
                        disabled={savingTeam}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-60"
                        data-testid="apps-save-team-default"
                      >
                        {savingTeam ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        {savingTeam ? 'Saving…' : 'Save as team default'}
                      </button>
                    )}
                  </div>
                )}

                {/* Grid of tiles, grouped by category */}
                <div className="px-6 py-5 min-h-[180px]">
                  {grouped.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center py-10 text-slate-400">
                      <p className="text-[12px] font-medium text-slate-500">Everything is pinned</p>
                      <p className="text-[11px] mt-1">Drag any sidebar icon onto this card to unpin it.</p>
                    </div>
                  ) : grouped.map(([cat, items]) => (
                    <section key={cat} className="mb-6 last:mb-0">
                      <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400 mb-3">{cat}</p>
                      <div className="grid grid-cols-6 gap-1">
                        {items.map((app) => (
                          <AppTile
                            key={app.id}
                            app={app}
                            onClick={() => go(app)}
                            draggable={!!onAddToSidebar}
                            onAddToSidebar={onAddToSidebar}
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
